import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, Copy } from "lucide-react";
import type { PlayerFlagRow } from "@/lib/api";
import { parseReasons, SOURCE_LABEL } from "@/components/FlagDetails";
import { formatTimestamp } from "@/lib/utils";

const playerHref = (id: string) => `/accounts?player=${encodeURIComponent(id)}`;

function prettyPayload(value: string | null): string {
  if (!value) return "—";
  try { return JSON.stringify(JSON.parse(value), null, 2); }
  catch { return value; }
}

function Payload({ payload }: { payload: string | null }) {
  const [copy, setCopy] = useState<"idle" | "copied" | "failed">("idle");
  const text = prettyPayload(payload);
  return <details className="mo-payload">
    <summary><ChevronRight size={13} />Latest payload
      <button type="button" className="pw-button mo-copy" disabled={!payload} onClick={e => { e.preventDefault(); navigator.clipboard.writeText(text).then(() => setCopy("copied"), () => setCopy("failed")); }}>
        {copy === "copied" ? <Check size={12} /> : <Copy size={12} />}{copy === "copied" ? "Copied" : "Copy"}</button>
      {copy === "failed" && <span role="alert" className="mo-copy-failed">Copy failed</span>}
    </summary>
    <pre>{text}</pre>
  </details>;
}

/** Retained distinct findings for one player (workspace version of FlagDetails). */
export function Findings({ rows }: { rows: PlayerFlagRow[] }) {
  if (!rows.length) return <p className="ws-muted">No detailed findings retained.</p>;
  return <ol className="mo-findings">{rows.map(row => {
    const reasons = parseReasons(row.reasons);
    return <li key={row.id}>
      <div className="mo-finding-meta">
        <span className="pw-badge">{SOURCE_LABEL[row.source] ?? row.source}</span>
        <span>{formatTimestamp(row.ts)}</span>
        <span>{row.occurrence_count}×</span>
        {row.reporter_id && <span>by <Link to={playerHref(row.reporter_id)} className="ws-mono">{row.reporter_id}</Link></span>}
      </div>
      {reasons.length ? <ul className="mo-reasons">{reasons.map((reason, i) => <li key={i}>{reason}</li>)}</ul> : <span className="ws-muted">—</span>}
      <Payload payload={row.payload} />
    </li>;
  })}</ol>;
}
